import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, Users, Copy, Check, Share2,
  MessagesSquare, Send, Facebook, Mail, UserPlus
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { tenantSlugFromHost } from '../../utils/tenantDomains';

const formatMoney = (value) => `$${Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})}`;

const formatFp = (value) => `${Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})} FP`;

const formatPercent = (value) => `${Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})}%`;

function getTenantHostSlug() {
  return tenantSlugFromHost();
}

export default function ReferralPage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const { user } = useAuth();
  const hostTenantSlug = getTenantHostSlug();
  const tenantPathPrefix = hostTenantSlug ? '' : (tenantSlug ? `/${tenantSlug}` : '');
  const explorePath = `${tenantPathPrefix}/app/explore`;
  const [copied, setCopied] = useState(null); // 'code' | 'link'
  const [brandName, setBrandName] = useState('Mystic Reels');
  const [stats, setStats] = useState({
    referral_code: '',
    total_referrals: 0,
    cash_earned: 0,
    freeplay_earned: 0,
  });
  const [rules, setRules] = useState({
    player_referrer_bonus_sc: 0,
    player_referred_bonus_sc: 0,
    player_referrer_load_percent: 0,
    player_referrer_load_one_time: false,
    player_referral_enabled: true,
  });

  // Generate a deterministic-ish referral code from username/email
  const fallbackCode = (user?.username || user?.email || 'PLAYER1')
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()
    .slice(0, 6) + 'VIP';
  const code = String(stats.referral_code || fallbackCode).toUpperCase();
  const encodedCode = encodeURIComponent(code);
  const link = `${window.location.origin}${tenantPathPrefix}/auth?mode=register&ref=${encodedCode}&preview=cosmic2`;

  useEffect(() => {
    let mounted = true;
    api.get('/api/referrals/stats')
      .then(res => { if (mounted && res?.data) setStats(prev => ({ ...prev, ...res.data })); })
      .catch(() => { /* keep local fallback */ });
    api.get('/api/referrals/rules')
      .then(res => { if (mounted && res?.data) setRules(prev => ({ ...prev, ...res.data })); })
      .catch(() => { /* keep local fallback */ });
    api.get('/api/branding')
      .then(res => { if (mounted && res?.data?.brand_name) setBrandName(res.data.brand_name); })
      .catch(() => { /* keep fallback */ });
    return () => { mounted = false; };
  }, []);

  const copy = async (text, kind) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1800);
    } catch (e) {
      console.warn(e);
    }
  };

  const totalReferrals = stats.total_referrals ?? stats.referral_count ?? 0;
  const cashEarned = stats.cash_earned ?? stats.total_cash_earned ?? stats.total_earned ?? 0;
  const freeplayEarned = stats.freeplay_earned ?? stats.fp_earned ?? stats.total_freeplay_earned ?? 0;
  const loadPercent = rules.player_referrer_load_percent ?? 0;
  const oneTimeLoadReward = rules.player_referrer_load_one_time === true;
  const referrerFp = rules.player_referrer_bonus_sc ?? 0;
  const referredFp = rules.player_referred_bonus_sc ?? 0;
  const sameSignupFp = Number(referrerFp) === Number(referredFp);

  return (
    <div
      className="player-app min-h-screen pb-12"
      style={{ color: '#FFFFFF' }}
      data-testid="explore-sub-referral"
    >
      {/* Header */}
      <div
        className="sticky top-0 z-30 px-4 sm:px-6 py-4 flex items-center gap-3"
        style={{
          background: 'rgba(6,23,14,0.85)',
          backdropFilter: 'blur(18px) saturate(160%)',
          WebkitBackdropFilter: 'blur(18px) saturate(160%)',
          borderBottom: '1px solid rgba(0,107,133,0.45)',
        }}
      >
        <button
          onClick={() => navigate(explorePath)}
          className="w-10 h-10 rounded-full flex items-center justify-center transition active:scale-95"
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#FFFFFF',
          }}
          data-testid="referral-back"
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1
            className="display font-extrabold text-xl sm:text-2xl flex items-center gap-2"
            style={{ color: '#FFFFFF' }}
          >
            <span
              className="w-9 h-9 rounded-xl flex items-center justify-center referral-bell-anim"
              style={{
                background: 'linear-gradient(135deg, #006B85, #004D61)',
                boxShadow: '0 6px 16px rgba(0,107,133,0.55), inset 0 1px 0 rgba(255,255,255,0.45)',
              }}
              aria-hidden
            >
              <Users className="w-5 h-5 text-white" strokeWidth={2.6} />
            </span>
            Referral &amp; Earn
          </h1>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Invite friends, get bonuses on every signup
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Hero / How it works card */}
        <div
          className="rounded-3xl p-6 sm:p-8 mb-6 relative overflow-hidden"
          style={{
            background:
              'linear-gradient(135deg, rgba(0,107,133,0.18) 0%, rgba(236,72,153,0.10) 100%)',
            border: '1px solid rgba(0,107,133,0.45)',
            boxShadow: '0 8px 22px rgba(0,107,133,0.18), inset 0 1px 0 rgba(255,255,255,0.05)',
          }}
        >
          <div className="flex flex-col sm:flex-row items-center gap-4 mb-6">
            <span
              className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0"
              style={{
                background: 'linear-gradient(135deg, #006B85, #EC4899)',
                boxShadow: '0 12px 26px rgba(0,107,133,0.55)',
              }}
            >
              <UserPlus className="w-8 h-8 text-white" strokeWidth={2.5} />
            </span>
            <div className="flex-1 text-center sm:text-left">
              <p className="display text-xs uppercase tracking-[0.2em] font-bold" style={{ color: '#C4B5FD' }}>
                Earn together
              </p>
              <h2 className="display font-extrabold text-2xl sm:text-3xl" style={{ color: '#FFFFFF' }}>
                Get <span style={{ color: '#FCD34D' }}>{formatPercent(loadPercent)}</span>{' '}
                {oneTimeLoadReward ? "on each friend's first load" : 'when your friends load'}
              </h2>
              <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.72)' }}>
                {sameSignupFp ? (
                  <>
                    Plus <span className="font-extrabold" style={{ color: '#FCD34D' }}>{formatFp(referredFp)}</span>{' '}
                    Freeplay bonuses for both of you.
                  </>
                ) : (
                  <>
                    Plus <span className="font-extrabold" style={{ color: '#FCD34D' }}>{formatFp(referrerFp)}</span>{' '}
                    Freeplay for you and <span className="font-extrabold" style={{ color: '#FCD34D' }}>{formatFp(referredFp)}</span> for your friend.
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Code + Link */}
          <div className="space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: '#C4B5FD' }}>
                Your Referral Code
              </p>
              <div
                className="flex items-stretch rounded-[22px] overflow-hidden"
                style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <button
                  type="button"
                  onClick={() => copy(code, 'code')}
                  className="flex-1 px-4 py-3 display font-extrabold text-xl mono tracking-wider text-left active:scale-[0.99] transition"
                  style={{ color: '#FCD34D' }}
                  data-testid="ref-code"
                >
                  {code}
                </button>
                <button
                  onClick={() => copy(code, 'code')}
                  className="px-4 display font-bold text-sm transition active:scale-95 flex items-center gap-1.5"
                  style={{
                    background: 'linear-gradient(180deg, #006B85, #004D61)',
                    color: '#fff',
                  }}
                  data-testid="ref-code-copy"
                >
                  {copied === 'code' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied === 'code' ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: '#C4B5FD' }}>
                Your Invite Link
              </p>
              <div
                className="flex items-stretch rounded-[22px] overflow-hidden"
                style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <button
                  type="button"
                  onClick={() => copy(link, 'link')}
                  className="flex-1 px-4 py-3 text-sm font-medium truncate text-left active:scale-[0.99] transition"
                  style={{ color: '#E9D5FF' }}
                  data-testid="ref-link"
                >
                  {link}
                </button>
                <button
                  onClick={() => copy(link, 'link')}
                  className="px-4 display font-bold text-sm transition active:scale-95 flex items-center gap-1.5"
                  style={{
                    background: 'linear-gradient(180deg, #006B85, #004D61)',
                    color: '#fff',
                  }}
                  data-testid="ref-link-copy"
                >
                  {copied === 'link' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied === 'link' ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </div>

          {/* Quick share row */}
          <div className="mt-5 flex flex-wrap gap-2">
            <ShareBtn
              href={`https://wa.me/?text=${encodeURIComponent(`Join me on ${brandName}! ` + link)}`}
              label="WhatsApp"
              Icon={MessagesSquare}
              color="#25D366"
            />
            <ShareBtn
              href={`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(`Join me on ${brandName}!`)}`}
              label="Telegram"
              Icon={Send}
              color="#229ED9"
            />
            <ShareBtn
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`}
              label="Facebook"
              Icon={Facebook}
              color="#1877F2"
            />
            <ShareBtn
              href={`mailto:?subject=${encodeURIComponent(`Join ${brandName}`)}&body=${encodeURIComponent('Sign up with my code ' + code + ' — ' + link)}`}
              label="Email"
              Icon={Mail}
              color="#EF4444"
            />
            <button
              onClick={async () => {
                if (navigator.share) {
                  try { await navigator.share({ title: brandName, text: 'Join me!', url: link }); } catch {}
                } else {
                  copy(link, 'link');
                }
              }}
              className="display font-bold text-xs px-4 py-2.5 rounded-full flex items-center gap-1.5 transition active:scale-95"
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.15)',
                color: '#fff',
              }}
              data-testid="ref-share-native"
            >
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Stat label="Total Referrals" value={Number(totalReferrals || 0).toLocaleString()} accent="#006B85" />
          <Stat label="Cash Earned" value={formatMoney(cashEarned)} accent="#22C55E" />
          <Stat label="Freeplay Earned" value={formatFp(freeplayEarned)} accent="#F59E0B" />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div
      className="rounded-[26px] p-3 sm:p-4 text-center"
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: `1px solid ${accent}55`,
      }}
    >
      <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: accent }}>
        {label}
      </p>
      <p
        className="display font-extrabold text-xl sm:text-2xl mt-0.5 mono"
        style={{ color: '#FFFFFF' }}
      >
        {value}
      </p>
    </div>
  );
}

function ShareBtn({ href, label, Icon, color }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="display font-bold text-xs px-4 py-2.5 rounded-full flex items-center gap-1.5 transition active:scale-95 hover:brightness-110"
      style={{
        background: `${color}22`,
        border: `1px solid ${color}55`,
        color,
      }}
      data-testid={`ref-share-${label.toLowerCase()}`}
    >
      <Icon className="w-4 h-4" /> {label}
    </a>
  );
}
